import React from 'react';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">About EmotionWise</h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Built for developers who need emotion and sarcasm signals inside real products.
          </p>
        </div>

        <div className="bg-slate-900/85 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 p-6 sm:p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">What EmotionWise Delivers</h2>
            <p className="text-slate-300 leading-relaxed">
              EmotionWise turns plain text into actionable signals: detected emotions, confidence scores, sarcasm detection, and language. You can test quickly in the web app, then call the same API from your services with X-API-Key auth.
            </p>
            <p className="mt-3 text-xs sm:text-sm text-amber-200">
              Note: sarcasm detection is experimental and should be validated with context.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Why Developers Choose It</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4">
                <h3 className="font-semibold text-indigo-200 mb-1">Production-Focused Detection</h3>
                <p className="text-sm text-indigo-200/90">Consistent emotion + sarcasm outputs with confidence for safer automation.</p>
              </div>
              <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-4">
                <h3 className="font-semibold text-purple-200 mb-1">One API, Two Access Modes</h3>
                <p className="text-sm text-purple-200/90">API key auth for server-to-server calls, plus a web playground for quick testing — same endpoint, same output.</p>
              </div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                <h3 className="font-semibold text-emerald-200 mb-1">Feedback-Ready Pipeline</h3>
                <p className="text-sm text-emerald-200/90">Optional voting and feedback endpoints to collect retraining-quality data.</p>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
                <h3 className="font-semibold text-amber-200 mb-1">Predictable Pricing Controls</h3>
                <p className="text-sm text-amber-200/90">Quota-based plans, Stripe subscriptions, and usage controls built in.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">How We Build</h2>
            <p className="text-slate-300 leading-relaxed">
              We prioritize fast integration, predictable behavior, and privacy-first defaults. We do not store any user-submitted text or analysis results — all processing happens in real time and data is discarded immediately. EmotionWise evolves from real developer feedback, integration testing, and production reliability goals.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-100 mb-3">Related Product</h2>
            <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4">
              <p className="text-sm text-sky-100">
                Need social media analytics?{' '}
                <a
                  href="https://www.emotionwise.social/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-sky-200"
                >
                  EmotionWise Social
                </a>{' '}
                helps you analyze Instagram comments with the same EmotionWise AI engine.
              </p>
            </div>
          </section>

          <div className="border-t border-slate-700 pt-6 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
